import { styled } from '@linaria/react';
import { Collapse } from '@mantine/core';
import { Fragment, type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from 'react';
import { newCOLORS } from '../../../../styles/colors';
import { formatJson } from '../flora-tool-call/flora-tool-call.helpers';
import { isBlockingAutoModeOutcome, isToolCallInFlight, toolCallInputText } from '../../flora-tool-input-streaming';
import type { ToolCall } from '../../flora.type';
import { getAutoModeTag, getToolVendor, summarizeVendors } from './agent-tool-call-group.helpers';
import { ToolCallTagVariant } from './chat.constants';

interface AgentToolCallGroupProps {
  /** A contiguous run of top-level tool calls, in the order Flora ran them. */
  toolCalls: ToolCall[];
  /**
   * Whether this turn is still streaming. Only a live turn shows the running
   * spinner; once streaming ends the group is always treated as settled, so a
   * stray never-terminated `pending` status cannot pin it in a spinner forever.
   */
  isLive: boolean;
}

/** Fire `action` on Enter/Space so the div toggles are keyboard-operable. */
function handleActivationKey(event: ReactKeyboardEvent, action: () => void) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
}

interface LiveToolInputProps {
  /** Raw argument fragments accumulated so far. */
  partialInput: string;
}

/**
 * Live view of a tool call's arguments while the model is still writing them.
 * Renders the raw accumulated fragments and keeps the newest text in view so a
 * long Page or document input reads like a typewriter rather than a frozen box.
 *
 * @param {LiveToolInputProps} props - The accumulated argument fragments.
 * @returns {JSX.Element} The auto-scrolling live input panel.
 */
function LiveToolInput({ partialInput }: LiveToolInputProps) {
  const preRef = useRef<HTMLPreElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-scroll whenever new fragments arrive
  useEffect(() => {
    const element = preRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [partialInput]);

  return (
    <IO>
      <IOLabel>Writing input…</IOLabel>
      <Pre ref={preRef} data-testid="live-tool-input" aria-live="polite">
        {partialInput}
      </Pre>
    </IO>
  );
}

/**
 * Renders a contiguous run of Flora's tool calls as one borderless block.
 *
 * While the turn is live and any call is still pending the block shows a
 * spinner line ("Working across Jira and Google Slides…") with the tools
 * appearing beneath as they run. Once the run settles it collapses to a single
 * summary line ("Worked across … · N tools") that expands to a numbered list,
 * and each row expands further to its raw input/output.
 *
 * @param {AgentToolCallGroupProps} props - The tool calls and live-turn flag.
 * @returns {JSX.Element | null} The group, or null when there are no calls.
 */
export function AgentToolCallGroup({ toolCalls, isLive }: AgentToolCallGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const [openTools, setOpenTools] = useState<Record<string, boolean>>({});

  if (toolCalls.length === 0) {
    return null;
  }

  const isRunning = isLive && toolCalls.some(isToolCallInFlight);
  const vendorSummary = summarizeVendors(toolCalls.map((toolCall) => getToolVendor(toolCall.tool_name)));
  const total = toolCalls.length;
  const showList = isRunning || expanded;

  const toggleTool = (id: string) => setOpenTools((current) => ({ ...current, [id]: !current[id] }));

  return (
    <Group>
      {isRunning ? (
        <RunningHeader>
          <Spinner />
          <RunningLabel>{vendorSummary ? `Working across ${vendorSummary}…` : 'Working…'}</RunningLabel>
          <Count>{total}</Count>
        </RunningHeader>
      ) : (
        <SummaryHeader
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          onKeyDown={(event) => handleActivationKey(event, () => setExpanded((current) => !current))}
        >
          <Chevron width="12" height="12" viewBox="0 0 24 24" fill="none" $open={expanded} aria-hidden="true">
            <path
              d="M6 9l6 6 6-6"
              stroke="var(--ink-lighter)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Chevron>
          <SummaryLabel>
            {vendorSummary ? `Worked across ${vendorSummary} · ` : ''}
            {total} {total === 1 ? 'tool' : 'tools'}
          </SummaryLabel>
        </SummaryHeader>
      )}

      {showList && (
        <List>
          {toolCalls.map((toolCall, index) => {
            const isError = toolCall.status === 'error';
            const isStreaming = isRunning && toolCall.status === 'streaming';
            const isDrafted = toolCall.status === 'drafted';
            const autoModeTag = getAutoModeTag(toolCall.auto_mode);
            // A blocked / unavailable tag explains why the call never ran, so it
            // replaces the generic "Drafted" tag rather than sitting beside it.
            const isBlockedByAutoMode = isBlockingAutoModeOutcome(toolCall.auto_mode);
            const isOpen = Boolean(openTools[toolCall.id]);
            const num = String(index + 1).padStart(2, '0');

            return (
              <Fragment key={toolCall.id}>
                <Row
                  className="fl-row-enter"
                  $interactive={!isRunning}
                  data-interactive={!isRunning}
                  role={isRunning ? undefined : 'button'}
                  tabIndex={isRunning ? undefined : 0}
                  aria-expanded={isRunning ? undefined : isOpen}
                  onClick={isRunning ? undefined : () => toggleTool(toolCall.id)}
                  onKeyDown={
                    isRunning ? undefined : (event) => handleActivationKey(event, () => toggleTool(toolCall.id))
                  }
                >
                  <Num>{num}</Num>
                  <Name $isError={isError}>{toolCall.tool_name}</Name>
                  {isStreaming && <Tag>Writing…</Tag>}
                  {isDrafted && !isBlockedByAutoMode && <Tag>Drafted</Tag>}
                  {autoModeTag && (
                    <Tag $variant={autoModeTag.variant} title={autoModeTag.tooltip}>
                      {autoModeTag.label}
                    </Tag>
                  )}
                  {!isRunning && (
                    <RowChevron
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      $open={isOpen}
                      aria-hidden="true"
                    >
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="var(--ink-lighter)"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </RowChevron>
                  )}
                </Row>
                {isStreaming && <LiveToolInput partialInput={toolCall.partial_input ?? ''} />}
                {!isRunning && (
                  <Collapse expanded={isOpen}>
                    <IO>
                      <IOLabel>Input</IOLabel>
                      <Pre>{toolCallInputText(toolCall)}</Pre>
                      {isDrafted ? (
                        <>
                          <IOLabel>Output</IOLabel>
                          <Pre>{toolCall.tool_error ? `Not run: ${toolCall.tool_error}` : 'Not run'}</Pre>
                        </>
                      ) : (
                        <>
                          <IOLabel $isError={isError}>{isError ? 'Error' : 'Output'}</IOLabel>
                          <Pre $isError={isError}>
                            {isError ? (toolCall.tool_error ?? 'No error detail') : formatJson(toolCall.tool_output)}
                          </Pre>
                        </>
                      )}
                    </IO>
                  </Collapse>
                )}
              </Fragment>
            );
          })}
        </List>
      )}
    </Group>
  );
}

const Group = styled.div`
  margin: 8px 0;
`;

const RunningHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 8px;
  margin: 0 -8px;
`;

const Spinner = styled.span`
  width: 13px;
  height: 13px;
  border: 2px solid var(--primary-base);
  border-top-color: transparent;
  border-radius: 50%;
  animation: agent-tool-group-spin 0.8s linear infinite;
  flex-shrink: 0;

  @keyframes agent-tool-group-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RunningLabel = styled.span`
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--ink-dark);
  min-width: 0;
`;

const Count = styled.span`
  font-size: 12.5px;
  color: var(--ink-lighter);
  flex-shrink: 0;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 8px;
  margin: 0 -8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--flora-hover-subtle);
  }
`;

const Chevron = styled.svg<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.15s;
`;

const SummaryLabel = styled.span`
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--ink-light);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0 2px 6px;
`;

const Row = styled.div<{ $interactive: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  margin: 0 -8px;
  border-radius: 6px;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition: background 0.15s;

  &.fl-row-enter {
    animation: agent-tool-row-in 0.28s ease both;
  }

  &[data-interactive='true']:hover {
    background: var(--flora-hover-subtle);
  }

  @keyframes agent-tool-row-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Num = styled.span`
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-lighter);
  flex-shrink: 0;
`;

const Name = styled.span<{ $isError: boolean }>`
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 13px;
  color: ${({ $isError }) => ($isError ? newCOLORS.red : 'var(--ink-base)')};
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TAG_COLORS: Readonly<Record<ToolCallTagVariant, { color: string; background: string }>> = {
  [ToolCallTagVariant.Default]: { color: 'var(--primary-darkest)', background: 'var(--primary-lightest)' },
  [ToolCallTagVariant.Subtle]: { color: 'var(--ink-light)', background: 'var(--sky-lighter)' },
  [ToolCallTagVariant.Danger]: { color: 'var(--red-darkest)', background: 'var(--red-lightest)' },
};

const Tag = styled.span<{ $variant?: ToolCallTagVariant }>`
  font-size: 10.5px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  color: ${({ $variant }) => TAG_COLORS[$variant ?? ToolCallTagVariant.Default].color};
  background: ${({ $variant }) => TAG_COLORS[$variant ?? ToolCallTagVariant.Default].background};
`;

const RowChevron = styled.svg<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.15s;
`;

const IO = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0 8px 27px;
`;

const IOLabel = styled.span<{ $isError?: boolean }>`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $isError }) => ($isError ? newCOLORS.red : 'var(--ink-lighter)')};
`;

const Pre = styled.pre<{ $isError?: boolean }>`
  margin: 0;
  padding: 8px 10px;
  background: ${({ $isError }) => ($isError ? 'var(--flora-error-surface)' : 'var(--sky-lighter)')};
  border-radius: 6px;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${({ $isError }) => ($isError ? newCOLORS.red : 'var(--ink-dark)')};
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 200px;
`;
